import React from "react";
import styles from "./Cart.module.css";
import { CategoryCardProps } from "@model/Category.model";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (process.env.REACT_APP_BASE_URL ?? "").replace(/\/+$/, "");

const isAbsoluteResourceUrl = (url: string) =>
  /^https?:\/\//i.test(url) ||
  url.startsWith("data:") ||
  url.startsWith("blob:");

const buildCategoryImageSrc = (imageUrl?: string | null) => {
  if (!imageUrl) return null;
  if (isAbsoluteResourceUrl(imageUrl)) return imageUrl;
  const normalizedPath = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
  return API_BASE_URL ? `${API_BASE_URL}${normalizedPath}` : normalizedPath;
};

const CategoryCard: React.FC<CategoryCardProps> = ({
  id,
  title,
  description,
  imageUrl,
  slug,
  onEdit,
  onDelete,
  productsCount,
}) => {
  const src = buildCategoryImageSrc(imageUrl);
  const navigate = useNavigate();
  const hasProducts = (productsCount ?? 0) > 0;
  return (
    <article
      className={styles["cc-cat-card"]}
      data-testid="category-card"
      aria-label={title}
    >
      <div className={styles["cc-cat-media"]}>
        {src ? (
          <img
            src={src}
            alt={title}
            loading="lazy"
            decoding="async"
            className={styles["cc-cat-image"]}
            width={240}
            height={160}
            data-testid="category-image"
          />
        ) : (
          <div
            className={styles["cc-cat-imagePlaceholder"]}
            aria-hidden
            data-testid="category-image-placeholder"
          />
        )}
      </div>

      <div className={styles["cc-cat-body"]}>
        <h3 className={styles["cc-cat-title"]} data-testid="category-title">
          {title}
        </h3>

        {!!description && (
          <p
            className={styles["cc-cat-description"]}
            data-testid="category-description"
          >
            {description}
          </p>
        )}

      </div>

      <div className={styles["cc-cat-footer"]}>
        <button
          type="button"
          className={`${styles["cc-cat-btn"]} ${styles["cc-cat-btnManageProducts"]}`}
          onClick={() => navigate(`/admin/categories/${slug}/products`)}
          data-testid="category-manage-products"
          aria-label={`Manage products for ${title}`}
        >
          Manage Products
          <span className={styles["cc-cat-btnSubText"]}>
            &#40;{productsCount}&#41;.
          </span>
        </button>

        <button
          type="button"
          className={`${styles["cc-cat-btn"]} ${styles["cc-cat-btnEdit"]}`}
          onClick={() => onEdit?.(id)}
          data-testid="category-edit"
          aria-label={`Edit ${title}`}
        >
          Edit
        </button>

        <button
          type="button"
          className={`${styles["cc-cat-btn"]} ${styles["cc-cat-btnDelete"]}`}
          onClick={() => onDelete?.(id)}
          data-testid="category-delete"
          aria-label={`Delete ${title}`}
          disabled={hasProducts}
        >
          Delete
        </button>
      </div>
    </article>
  );
};

export default CategoryCard;
